/** Settings → Notifications. Per-channel alert routing. */
import { useState } from "react";
import { Panel } from "@/components/surface/Panel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, FlaskConical } from "lucide-react";
import { ComingOnline, THead } from "./shared";
import {
  useNotifChannels, useNotifRoutes, useCreateNotifChannel, useDeleteNotifChannel, useTestNotifChannel,
  type NotifKind,
} from "@/hooks/security/useEdrAdmin";

const KINDS: { value: NotifKind; label: string; placeholder: string }[] = [
  { value: "slack", label: "Slack", placeholder: "#smplify-edr" },
  { value: "email", label: "Email", placeholder: "oncall@company.com" },
  { value: "pagerduty", label: "PagerDuty", placeholder: "routing key" },
  { value: "webhook", label: "Webhook", placeholder: "https://…" },
];

const SEVERITIES: Array<"info" | "low" | "medium" | "high" | "critical"> = ["info","low","medium","high","critical"];

export function NotificationsSection() {
  const chanQ = useNotifChannels();
  const routesQ = useNotifRoutes();
  const [addOpen, setAddOpen] = useState(false);

  const chanUnavail = chanQ.data?.kind === "unavailable";
  const channels = chanQ.data?.kind === "ok" ? chanQ.data.data.channels : [];
  const routes = routesQ.data?.kind === "ok" ? routesQ.data.data.routes : [];
  const del = useDeleteNotifChannel();
  const test = useTestNotifChannel();

  return (
    <div className="space-y-4">
      <Panel title={`Channels (${channels.length})`}
        actions={<Button size="sm" disabled={chanUnavail} onClick={() => setAddOpen(true)}><Plus className="h-3.5 w-3.5 mr-1" /> Add channel</Button>}
        loading={chanQ.isLoading}
        empty={!chanQ.isLoading && (chanUnavail || channels.length === 0)}
        emptyTitle={chanUnavail ? "Coming online — Q3 2026" : "No channels yet"}
        emptyDescription={chanUnavail ? "Notification endpoints aren't live yet." : "Add a Slack, email, or PagerDuty channel."}
      >
        <div className="overflow-x-auto -mx-4 -my-4">
          <table className="w-full text-xs">
            <THead cols={["Kind", "Target", "Severity ≥", "Quiet hours", ""]} />
            <tbody>
              {channels.map((c) => (
                <tr key={c.id} className="border-b border-border/60 hover:bg-muted/40">
                  <td className="px-3 py-2 uppercase text-[10px] text-muted-foreground">{c.kind}</td>
                  <td className="px-3 py-2 font-mono text-[11px]">{c.target}</td>
                  <td className="px-3 py-2 uppercase text-[10px]">{c.severityMin}</td>
                  <td className="px-3 py-2 text-muted-foreground">{c.quietHours ? `${c.quietHours.start}–${c.quietHours.end}` : "off"}</td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" className="h-7 px-2" disabled={test.isPending} onClick={() => test.mutate(c.id)}><FlaskConical className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { if (confirm(`Remove ${c.name}?`)) del.mutate(c.id); }}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel title={`Routing rules (${routes.length})`} loading={routesQ.isLoading}>
        {routes.length === 0 ? <p className="text-xs text-muted-foreground py-2">No custom routing rules. Channels above will receive everything matching their severity threshold.</p> : (
          <ul className="text-xs space-y-1">{routes.map((r) => <li key={r.id} className="font-mono text-muted-foreground">{r.description}</li>)}</ul>
        )}
      </Panel>

      <AddChannelDialog open={addOpen} onOpenChange={setAddOpen} />
    </div>
  );
}

function AddChannelDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const [kind, setKind] = useState<NotifKind>("slack");
  const [target, setTarget] = useState("");
  const [name, setName] = useState("");
  const [severityMin, setSeverityMin] = useState<"info" | "low" | "medium" | "high" | "critical">("critical");
  const create = useCreateNotifChannel();
  const placeholder = KINDS.find((k) => k.value === kind)?.placeholder;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a channel</DialogTitle>
          <DialogDescription>Channels receive alerts at or above the configured severity.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1.5"><Label className="text-xs">Kind</Label>
            <Select value={kind} onValueChange={(v) => setKind(v as NotifKind)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Display name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Eng on-call" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Target</Label>
            <Input value={target} onChange={(e) => setTarget(e.target.value)} placeholder={placeholder} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Minimum severity</Label>
            <Select value={severityMin} onValueChange={(v) => setSeverityMin(v as typeof severityMin)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{SEVERITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!name || !target || create.isPending} onClick={async () => {
            await create.mutateAsync({ kind, name, target, severityMin });
            onOpenChange(false); setName(""); setTarget("");
          }}>{create.isPending ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
